import { usePublicClient } from 'wagmi';
import { useQuery } from '@tanstack/react-query';
import { formatUnits } from 'viem';
import { UniswapV3OracleABI } from '../components/abi/UniswapV3OracleABI';
import { ContractRegistry } from '../configs/contractRegistry';
import { DynaVaultABI } from '../components/abi/DynavaultABI';
import { getDepositTokenList } from '../utils/depositTokenList';
import { queryKeys } from 'structure/pv2/vault/configs/queryKeys';

interface UseDepositTokenPriceParams {
  vaultAddress: `0x${string}`;
  vaultChainId: number;
  from: `0x${string}`;
}

const buildPriceCall = (params: {
  chainId: number;
  tokenAddress: `0x${string}`;
  oracleAddress: `0x${string}`;
}) => {
  const { chainId, tokenAddress, oracleAddress } = params;

  const registry =
    ContractRegistry[chainId as unknown as keyof typeof ContractRegistry];

  if (!registry) {
    throw new Error(`Contract registry not found for chainId: ${chainId}`);
  }

  const reference = registry.DefaultStableToken.address;
  const referenceDecimals = registry.DefaultStableToken.decimals;

  return {
    chainId,
    abi: UniswapV3OracleABI,
    functionName: 'getPrice',
    address: oracleAddress,
    args: [tokenAddress, reference],
    referenceDecimals,
  } as const;
};

export const useDepositTokenPrice = (params: UseDepositTokenPriceParams) => {
  const { vaultAddress, vaultChainId, from } = params;

  const publicClient = usePublicClient({
    chainId: vaultChainId,
  });

  return useQuery({
    queryKey: queryKeys.vaultDepositTokenPrice({
      vaultAddress,
      chainId: vaultChainId,
    }),
    refetchInterval: 14000,
    staleTime: 14000,
    queryFn: async () => {
      if (!publicClient) {
        throw new Error('Public client is not available');
      }

      // Get vault deposit token address
      const depositTokenAddress = await publicClient.readContract({
        abi: DynaVaultABI,
        account: from,
        address: vaultAddress,
        functionName: 'asset',
        authorizationList: undefined,
      });

      // Get oracle address
      const assetsOracleAddress = await publicClient.readContract({
        abi: DynaVaultABI,
        address: vaultAddress,
        account: from,
        functionName: 'referenceAssetOracle',
        authorizationList: undefined,
      });

      // Get deposit token info from our list
      const depositTokenList = getDepositTokenList(vaultChainId);
      const depositTokenInfo = depositTokenList.find(
        (token) =>
          token.address.toLowerCase() === depositTokenAddress.toLowerCase()
      );

      if (!depositTokenInfo) {
        throw new Error('Deposit token not found in list');
      }

      // Get price from oracle
      const priceCall = buildPriceCall({
        chainId: vaultChainId,
        oracleAddress: assetsOracleAddress,
        tokenAddress: depositTokenAddress,
      });

      const priceResult = await publicClient.readContract(priceCall);
      const price = Number(
        formatUnits(priceResult[0] as bigint, priceCall.referenceDecimals)
      );

      return {
        depositTokenAddress,
        depositTokenInfo,
        price,
        symbol: depositTokenInfo.symbol,
        decimals: depositTokenInfo.decimals,
      };
    },
  });
};
